/**
 * The heat strip (UX-SPEC §5.2): the joint-segment step function of the authorship index along the document on a fixed
 * 0–100 axis, or the per-chapter indices; chapter ticks, optional hotspot markers, the reader's position window, a
 * hover tooltip and a keyboard cursor. SVG in a 1000 × 100 viewBox stretched to the container; strokes are
 * non-scaling; everything stays well under 2,500 nodes.
 */
import { signal } from '@preact/signals';
import { useRef, useState } from 'preact/hooks';
import type { ChannelId } from '../../engine/channels/types.ts';
import { CHANNEL_IDS } from '../../engine/channels/types.ts';
import { lttb } from '../../shared/lttb.ts';
import type { SlimSegment, ViewModel } from '../../shared/viewmodel.ts';
import { evidenceFill } from '../color.ts';
import { focus } from '../focus.ts';
import { fmt1, fmtInt, fmtInterval } from '../format.ts';
import { derive, hotspotsInRange } from '../model.ts';
import { hashFor, navigate, setParams } from '../router.ts';
import { STRIP } from '../strings.ts';

export const stripMode = signal<'section' | 'chapter'>('section');
export const stripMarkers = signal(false);
/** The stretch of the document visible in the reader (character offsets), set by the reader. */
export const readerWindow = signal<{ a: number; b: number } | null>(null);

const W = 1000;
const MAX_CHAPTER_BARS = 250;

interface Item {
	seg: SlimSegment;
	title: string;
}

function itemsFor(vm: ViewModel, mode: 'section' | 'chapter'): Item[] {
	const list = mode === 'section' ? vm.ensemble.segments.joint : vm.ensemble.segments.units;
	return list.map((seg, i) => ({
		seg,
		title: seg.title ?? (mode === 'section' ? STRIP.section(i + 1) : STRIP.chapter(i + 1)),
	}));
}

function tipText(vm: ViewModel, it: Item): string {
	const hot = hotspotsInRange(
		vm.ensemble.hotspots.hotspots,
		it.seg.range.paraStart,
		it.seg.range.paraEnd
	);
	return STRIP.tip(
		it.title,
		`${fmtInt(it.seg.words)} words`,
		fmt1(it.seg.index),
		fmtInterval(it.seg.ci90),
		it.seg.ciMethod,
		STRIP.hotCount(hot)
	);
}

export function stripSummary(vm: ViewModel, mode: 'section' | 'chapter'): string {
	const list = mode === 'section' ? vm.ensemble.segments.joint : vm.ensemble.segments.units;
	if (mode === 'chapter') return STRIP.nameChapters(list.length);
	const values = list.map((s) => s.index);
	return STRIP.name(list.length, fmt1(Math.min(...values)), fmt1(Math.max(...values)));
}

function Markers({ vm, ids }: { vm: ViewModel; ids: ChannelId[] | null }) {
	const { bins, density, rows } = vm.strip;
	const rowsToSum = ids ? ids.map((id) => CHANNEL_IDS.indexOf(id)) : [rows - 1];
	const levels: number[] = new Array(bins).fill(0);
	let max = 0;
	const sums: number[] = new Array(bins).fill(0);
	for (const r of rowsToSum)
		for (let i = 0; i < bins; i++)
			sums[i] = (sums[i] as number) + (density[r * bins + i] as number);
	for (const v of sums) if (v > max) max = v;
	if (max === 0) return null;
	for (let i = 0; i < bins; i++) levels[i] = Math.ceil((4 * (sums[i] as number)) / max);
	const rects: Array<{ x: number; w: number; level: number }> = [];
	let i = 0;
	while (i < bins) {
		const lv = levels[i] as number;
		if (lv === 0) {
			i++;
			continue;
		}
		let j = i + 1;
		while (j < bins && levels[j] === lv) j++;
		rects.push({ x: (i / bins) * W, w: ((j - i) / bins) * W, level: lv });
		i = j;
	}
	const colour = ids && ids.length === 1 ? 'var(--cat-1)' : 'var(--ev-500)';
	return (
		<svg
			class="strip-markers"
			viewBox={`0 0 ${W} 10`}
			preserveAspectRatio="none"
			aria-hidden="true"
			focusable="false"
		>
			{rects.map((r) => (
				<rect
					key={r.x}
					x={r.x}
					y={0}
					width={Math.max(r.w, 1)}
					height={10}
					fill={colour}
					fill-opacity={0.25 + r.level * 0.19}
				/>
			))}
		</svg>
	);
}

export function HeatStrip({ vm, variant }: { vm: ViewModel; variant: 'sticky' | 'section' }) {
	const mode = stripMode.value;
	const total = Math.max(1, vm.text.length);
	const items = itemsFor(vm, mode);
	const [tip, setTip] = useState<{ text: string; x: number } | null>(null);
	const [cursor, setCursor] = useState<number | null>(null);
	const [live, setLive] = useState('');
	const svgRef = useRef<SVGSVGElement>(null);
	const drag = useRef<{ startX: number; moved: boolean; inWindow: boolean } | null>(null);
	const win = readerWindow.value;
	const x = (c: number) => (c / total) * W;
	const y = (v: number) => 100 - Math.max(0, Math.min(100, v));

	const charAt = (clientX: number): number => {
		const r = svgRef.current?.getBoundingClientRect();
		if (!r || r.width === 0) return 0;
		return Math.max(0, Math.min(total - 1, Math.floor(((clientX - r.left) / r.width) * total)));
	};
	const itemAt = (c: number): number =>
		items.findIndex((it) => c >= it.seg.range.charStart && c < it.seg.range.charEnd);
	const openAt = (c: number) => {
		const p = derive(vm).paraOf(c);
		navigate(hashFor('read', { params: { p: String(p), ch: focus.value.join(',') || undefined } }));
	};

	const onMove = (e: PointerEvent) => {
		const d = drag.current;
		if (d?.inWindow && win) {
			if (Math.abs(e.clientX - d.startX) > 3) d.moved = true;
			if (d.moved) setParams({ p: String(derive(vm).paraOf(charAt(e.clientX))) });
			return;
		}
		const c = charAt(e.clientX);
		const i = itemAt(c);
		const it = i >= 0 ? items[i] : undefined;
		const r = svgRef.current?.getBoundingClientRect();
		setTip(
			it && r
				? {
						text: tipText(vm, it),
						x: Math.min(Math.max(0, e.clientX - r.left), Math.max(0, r.width - 280)),
					}
				: null
		);
	};
	const onDown = (e: PointerEvent) => {
		const c = charAt(e.clientX);
		drag.current = { startX: e.clientX, moved: false, inWindow: !!win && c >= win.a && c <= win.b };
		(e.currentTarget as Element).setPointerCapture?.(e.pointerId);
	};
	const onUp = (e: PointerEvent) => {
		const d = drag.current;
		drag.current = null;
		if (d && !d.moved) openAt(charAt(e.clientX));
	};
	const onKey = (e: KeyboardEvent) => {
		const dir = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
		if (dir !== 0) {
			e.preventDefault();
			let list = items;
			let idx = cursor ?? -1;
			if (e.shiftKey && mode === 'section') {
				// Shift+arrow: chapter to chapter
				list = itemsFor(vm, 'chapter');
				const cur = cursor === null ? 0 : (items[cursor]?.seg.range.charStart ?? 0);
				idx = list.findIndex((it) => it.seg.range.charEnd > cur) - (dir > 0 ? 0 : 0);
				const next = Math.max(0, Math.min(list.length - 1, idx + dir));
				const it = list[next];
				if (it) {
					setLive(tipText(vm, it));
					const j = itemAt(it.seg.range.charStart);
					if (j >= 0) setCursor(j);
				}
				return;
			}
			const next = Math.max(0, Math.min(list.length - 1, idx + dir));
			setCursor(next);
			const it = list[next];
			if (it) setLive(tipText(vm, it));
		} else if ((e.key === 'Enter' || e.key === ' ') && cursor !== null) {
			e.preventDefault();
			const it = items[cursor];
			if (it) openAt(it.seg.range.charStart);
		}
	};

	// chapter bars, downsampled above 250 (UX-STRIP-03)
	const bars = (() => {
		if (mode !== 'chapter') return null;
		if (items.length <= MAX_CHAPTER_BARS) return { kind: 'bars' as const };
		const mid = items.map((it) => (it.seg.range.charStart + it.seg.range.charEnd) / 2);
		const pts = lttb(
			mid,
			items.map((it) => it.seg.index),
			MAX_CHAPTER_BARS
		);
		return { kind: 'line' as const, pts };
	})();

	const ids = focus.value.length ? focus.value : null;
	const cur = cursor !== null ? items[cursor] : undefined;
	return (
		<div class={`strip-wrap strip-${variant}`}>
			<div class="strip-axis" aria-hidden="true">
				<span>{STRIP.axis100}</span>
				<span>{STRIP.axis0}</span>
			</div>
			<div class="strip-main">
				<svg
					ref={svgRef}
					class="strip"
					viewBox={`0 0 ${W} 100`}
					preserveAspectRatio="none"
					role="img"
					tabindex={0}
					aria-label={stripSummary(vm, mode)}
					onPointerMove={onMove}
					onPointerLeave={() => setTip(null)}
					onPointerDown={onDown}
					onPointerUp={onUp}
					onKeyDown={onKey}
					onBlur={() => setCursor(null)}
					data-testid="heat-strip"
					data-points={items.length}
				>
					{[25, 50, 75].map((g) => (
						<line
							key={g}
							x1={0}
							x2={W}
							y1={y(g)}
							y2={y(g)}
							stroke="var(--stone-300)"
							stroke-width="1"
							vector-effect="non-scaling-stroke"
						/>
					))}
					{bars?.kind === 'line' ? (
						<polyline
							points={Array.from(
								bars.pts.x,
								(px, i) => `${x(px)},${y(bars.pts.y[i] as number)}`
							).join(' ')}
							fill="none"
							stroke="var(--ev-700)"
							stroke-width="2"
							vector-effect="non-scaling-stroke"
						/>
					) : (
						items.map((it) => {
							const a = x(it.seg.range.charStart);
							const w = Math.max(0.4, x(it.seg.range.charEnd) - a);
							return (
								<g
									key={`${mode}-${it.seg.ordinal}`}
									class="strip-seg"
									data-ordinal={it.seg.ordinal}
								>
									<rect
										x={a}
										y={y(it.seg.ci90[1])}
										width={w}
										height={Math.max(0.5, it.seg.ci90[1] - it.seg.ci90[0])}
										fill="var(--ev-100)"
									/>
									<rect
										x={a}
										y={y(it.seg.index)}
										width={w}
										height={it.seg.index}
										fill={evidenceFill(it.seg.index)}
									/>
									<line
										x1={a}
										x2={a + w}
										y1={y(it.seg.index)}
										y2={y(it.seg.index)}
										stroke="var(--ev-700)"
										stroke-width="2"
										vector-effect="non-scaling-stroke"
									/>
									{mode === 'section' ? (
										<line
											x1={a}
											x2={a}
											y1={0}
											y2={100}
											stroke="var(--sand-100)"
											stroke-width="2"
											vector-effect="non-scaling-stroke"
										/>
									) : null}
								</g>
							);
						})
					)}
					{cur ? (
						<rect
							x={x(cur.seg.range.charStart)}
							y={0}
							width={Math.max(1, x(cur.seg.range.charEnd) - x(cur.seg.range.charStart))}
							height={100}
							fill="none"
							stroke="var(--moss-700)"
							stroke-width="2"
							vector-effect="non-scaling-stroke"
						/>
					) : null}
					{win ? (
						<rect
							class="strip-window"
							x={x(win.a)}
							y={1}
							width={Math.max(2, x(win.b) - x(win.a))}
							height={98}
							fill="var(--moss-100)"
							fill-opacity="0.45"
							stroke="var(--moss-700)"
							stroke-width="2"
							vector-effect="non-scaling-stroke"
						>
							<title>{STRIP.windowLabel}</title>
						</rect>
					) : null}
				</svg>
				<svg
					class="strip-ticks"
					viewBox={`0 0 ${W} 8`}
					preserveAspectRatio="none"
					aria-hidden="true"
					focusable="false"
				>
					{vm.chapters.length > 1
						? vm.chapters.map((c) => (
								<line
									key={c.charStart}
									x1={x(c.charStart)}
									x2={x(c.charStart)}
									y1={0}
									y2={8}
									stroke="var(--stone-500)"
									stroke-width="1"
									vector-effect="non-scaling-stroke"
								/>
							))
						: null}
				</svg>
				{stripMarkers.value ? <Markers vm={vm} ids={ids} /> : null}
				{tip ? (
					<div
						class="strip-tip"
						role="presentation"
						ref={(el) => el?.style.setProperty('left', `${tip.x}px`)}
					>
						{tip.text}
					</div>
				) : null}
				<div class="visually-hidden" aria-live="polite" data-testid="strip-live">
					{live}
				</div>
			</div>
		</div>
	);
}
